// components/Explore department.tsx
import React, { Dispatch, SetStateAction } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Paper,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  AccessTime,
  Psychology,
  ArrowForward,
  ManageAccounts
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ArrowBackTwoToneIcon from '@mui/icons-material/ArrowBackTwoTone';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentPermitDetails } from 'src/redux-store/slices/currentPermitSlice';
import PersonIcon from '@mui/icons-material/Person';
import { RootState } from 'src/redux-store/appStore';
import AddNewPet from '../Permits/PetPermit/AddNewPet';
import { getLocalData } from 'src/utils/CommonFunctions';

interface Permit {
  PermitID: number;
  PermitName: string;
  PermitDescription: string;
  UserRoleID: number;
  UserRoleName: string;
}

export interface Department {
  DepartmentID: number;
  DepartmentName: string;
  Permits: Permit[];
}

interface PropTypes {
  currentDepartment: Department;
  setExploreDepartment: Dispatch<SetStateAction<boolean>>;
}

const ExploreDepartment: React.FC<PropTypes> = ({
  currentDepartment,
  setExploreDepartment
}) => {
  const navigate = useNavigate();
  const userDetails = useSelector((store: RootState) => store.user.userDetails);
  const [open, setOpen] = React.useState(false);
  const petCount = getLocalData('petCount');

  // features object
  const features = [
    {
      icon: <ManageAccounts sx={{ fontSize: 40, color: '#2196F3' }} />,
      title: 'Multi-Department Support',
      description: 'Engineering, Clerk, Planning & more'
    },
    {
      icon: <Psychology sx={{ fontSize: 40, color: '#2196F3' }} />,
      title: 'Smart Processing',
      description: 'Automated permit review workflow'
    },
    {
      icon: <AccessTime sx={{ fontSize: 40, color: '#2196F3' }} />,
      title: 'Real-time Tracking',
      description: 'Monitor application status live'
    },
    {
      icon: <AccessTime sx={{ fontSize: 40, color: '#2196F3' }} />,
      title: 'Real-time Tracking',
      description: 'Monitor application status live'
    }
  ];
  const dispatch = useDispatch();

  // Handler to create new application to the respective permit
  const handleNewApplication = (permit) => {
    const dName = currentDepartment?.DepartmentName;
    dispatch(
      setCurrentPermitDetails({
        id: permit?.PermitID,
        townshipId: userDetails?.TownshipID,
        departmentName: dName,
        currentPermitUserRole: permit?.UserRoleName
      })
    );
    navigate(
      `/${userDetails?.TownshipID}/${dName}/${permit?.PermitID}/new-application`
    );
  };

  // Handler to view respective permit application
  const handleViewApplication = (permit) => {
    const dName = currentDepartment?.DepartmentName;
    dispatch(
      setCurrentPermitDetails({
        id: permit?.PermitID,
        townshipId: userDetails?.TownshipID,
        departmentName: dName,
        currentPermitUserRole: permit?.UserRoleName
      })
    );
    navigate(
      `/${userDetails?.TownshipID}/${dName}/${permit?.PermitID}/application-list`
    );
  };

  // Handler to go back on dashboard
  const handleBack = () => {
    setExploreDepartment(false);
  };

  return (
    <Box
      className="dashboard_department_container"
      sx={{
        minHeight: 'calc(100vh - 72px)',
        display: 'flex',
        overflow: 'hidden',
        pt: 1       
      }}
    >
      <Container
        maxWidth="lg"
        sx={{ position: 'relative', zIndex: 1, px: { xs: 0 } }}
      >
        <Grid
          container
          spacing={1}
          pb={2}
          alignItems="center"
          sx={{ flexDirection: 'column' }}
        >
          <Grid item xs={12} md={12}>
            <Box sx={{ py: { md: 4, xs: 2 }, display: 'flex' }}>
              {/* Back arrow container */}
              <Tooltip arrow placement="top" title="Go back">
                <IconButton
                  color="primary"
                  sx={{
                    padding: '14px',
                    mr: 2,
                    backgroundColor: 'revert',
                    transition: 'transform 0.3s ease',
                    marginTop: '-3px',
                    height: '40px',
                    alignSelf: 'center',
                    '&:hover': {
                      transform: 'translateX(-5px)',
                      backgroundColor: 'revert'
                    }
                  }}
                  onClick={handleBack}
                >
                  <ArrowBackTwoToneIcon />
                </IconButton>
              </Tooltip>
              <Typography
                variant="h2"
                color="white"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.6rem', sm: '2.5rem' },
                  lineHeight: 1.2,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                  textAlign: 'center'
                }}
              >
                {currentDepartment?.DepartmentName}               
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={12}>
            <Grid container spacing={2} justifyContent={'center'}>
              {currentDepartment?.Permits?.length > 0 &&
                currentDepartment?.Permits.map((permit, index) => (
                  <Grid item xs={12} lg={6} md={6} sm={6} key={index}>
                    <Paper
                      elevation={0}
                      sx={{
                        position: 'relative',
                        p: { md: 3, xs: 2 },
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        borderRadius: 2,
                        alignItems: 'center',
                        gap: 2,
                        transition: 'transform 0.3s ease',
                        transformOrigin: 'center center',
                        '&:hover': {
                          transform: 'scale(1.03)'
                        }
                      }}
                    >
                      {permit?.UserRoleName === 'Manager' ? (
                        <Tooltip
                          arrow
                          placement="top"
                          title="You are manager for this department."
                          sx={{ position: 'absolute', top: 0, right: 0 }}
                        >
                          <IconButton color="primary">
                            <PersonIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      <Box textAlign={'center'}>{features[index]?.icon}</Box>
                      <Box>
                        {/* Permit name conatiner */}
                        <Typography
                          variant="h4"
                          fontSize={'21px'}
                          textAlign={'center'}
                          sx={{ fontWeight: 600, color: '#1976D2' }}
                        >
                          {permit?.PermitName}
                        </Typography>
                        {/* Permit description conatiner */}
                        <Typography
                          className="view-more"
                          variant="body2"
                          sx={{ color: '#546E7A', my: 2 }}
                          dangerouslySetInnerHTML={{
                            __html: permit?.PermitDescription
                          }}
                        />
                        {/* Permit button conatiner */}
                        <Box
                          sx={{
                            display: { xs: 'inherit', sm: 'grid', md: 'flex' },
                            gap: 2,
                            justifyContent: 'center'
                          }}
                          className="00000000000"
                        >
                          {permit?.PermitName !== 'Pet License' ||
                          permit?.UserRoleName === 'Manager' ? (
                            <Button
                              variant="contained"
                              size="medium"
                              endIcon={<ArrowForward />}
                              sx={{
                                backgroundColor: 'white',
                                color: '#2196F3',
                                width: { md: 'auto', xs: '100%' },
                                fontWeight: 600,
                                boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                                '&:hover': {
                                  backgroundColor: '#eaedf1',
                                  boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                                }
                              }}
                              onClick={() => handleViewApplication(permit)}
                            >
                              View Applications
                            </Button>
                          ) : (
                            <>
                              {parseInt(petCount) > 0 && (
                                <Button
                                  variant="contained"
                                  size="medium"
                                  endIcon={<ArrowForward />}
                                  sx={{
                                    backgroundColor: 'white',
                                    color: '#2196F3',
                                    width: { md: 'auto', xs: '100%' },
                                    fontWeight: 600,
                                    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                                    '&:hover': {
                                      backgroundColor: '#eaedf1',
                                      boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                                    }
                                  }}
                                  onClick={() => handleViewApplication(permit)}
                                >
                                  View Applications
                                </Button>
                              )}
                            </>
                          )}
                          {permit?.PermitName !== 'Pet License' ||
                          permit?.UserRoleName === 'Manager' ? (
                            <Button
                              variant="contained"
                              size="medium"
                              endIcon={<ArrowForward />}
                              sx={{
                                backgroundColor: 'white',
                                color: '#2196F3',
                                fontWeight: 600,
                                mt: { md: 'auto', xs: 1 },
                                width: { md: 'auto', xs: '100%' },
                                boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                                '&:hover': {
                                  backgroundColor: '#eaedf1',
                                  boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                                }
                              }}
                              onClick={() => handleNewApplication(permit)}
                            >
                              New Application
                            </Button>
                          ) : (
                            <>
                              {parseInt(petCount) > 0 && (
                                <Button
                                  variant="contained"
                                  size="medium"
                                  endIcon={<ArrowForward />}
                                  sx={{
                                    backgroundColor: 'white',
                                    color: '#2196F3',
                                    fontWeight: 600,
                                    mt: { md: 'auto', xs: 1 },
                                    width: { md: 'auto', xs: '100%' },
                                    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                                    '&:hover': {
                                      backgroundColor: '#eaedf1',
                                      boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                                    }
                                  }}
                                  onClick={() => handleNewApplication(permit)}
                                >
                                  New Application
                                </Button>
                              )}
                            </>
                          )}

                          {permit?.PermitName === 'Pet License' &&
                            parseInt(petCount) < 1 && (
                              <Button
                                variant="contained"
                                size="medium"
                                endIcon={<ArrowForward />}
                                sx={{
                                  backgroundColor: 'white',
                                  color: '#2196F3',
                                  fontWeight: 600,
                                  mt: { md: 'auto', xs: 1 },
                                  width: { md: 'auto', xs: '100%' },
                                  boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                                  '&:hover': {
                                    backgroundColor: '#eaedf1',
                                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                                  }
                                }}
                                onClick={() => setOpen(true)}
                              >
                                Add New Pet
                              </Button>
                            )}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
      {/* Add New Pet popup */}
      <AddNewPet setOpen={setOpen} open={open} />
    </Box>
  );
};

export default ExploreDepartment;
